<script lang="ts">
  import DashCard from '../shared/DashCard.svelte';
  import { Citas, type Cita, type CitaEstado } from '../../../lib/api';

  let {
    limit = 8,
    interactive = false,
  }: {
    limit?: number;
    interactive?: boolean;
  } = $props();

  let citas = $state<Cita[]>([]);
  let error = $state<string | null>(null);
  let busy = $state<string | null>(null);

  function hoyIso() {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }

  async function cargar() {
    error = null;
    try {
      const all = await Citas.listar(hoyIso());
      citas = all
        .filter(c => c.estado !== 'CANCELADA' && c.estado !== 'ATENDIDA')
        .slice(0, limit);
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  async function cambiarEstado(id: string | undefined, estado: CitaEstado) {
    if (!id) return;
    busy = id;
    try {
      await Citas.cambiarEstado(id, estado);
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      busy = null;
    }
  }

  function hora(iso: string) {
    try { return new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  }

  function badge(estado?: string) {
    if (estado === 'CONFIRMADA') return 'ok';
    if (estado === 'NO_ASISTIO') return 'danger';
    return '';
  }

  $effect(() => { cargar(); const t = setInterval(cargar, 60000); return () => clearInterval(t); });
</script>

<DashCard title="Citas hoy" hint="{citas.length} pendientes">
  {#snippet actions()}
    <button type="button" class="btn-sm" onclick={cargar}>↻</button>
  {/snippet}
  {#if error}<p class="err">{error}</p>{/if}
  {#if citas.length === 0 && !error}
    <p class="empty">Sin citas pendientes hoy.</p>
  {:else}
    <table class="dash-table">
      <thead>
        <tr>
          <th>Hora</th>
          <th>Paciente</th>
          <th>Profesional</th>
          <th>Estado</th>
          {#if interactive}<th>Acciones</th>{/if}
        </tr>
      </thead>
      <tbody>
        {#each citas as c (c.id)}
          <tr>
            <td class="dash-mono">{hora(c.fechaHora)}</td>
            <td><strong>{c.pacienteNombre}</strong></td>
            <td>{c.profesional ?? '—'}</td>
            <td><span class="dash-badge {badge(c.estado)}">{c.estado ?? 'PROGRAMADA'}</span></td>
            {#if interactive}
              <td class="acts">
                {#if c.estado === 'PROGRAMADA'}
                  <button type="button" class="btn-sm" disabled={busy === c.id} onclick={() => cambiarEstado(c.id, 'CONFIRMADA')}>Confirmar</button>
                {/if}
                {#if c.estado === 'CONFIRMADA' || c.estado === 'PROGRAMADA'}
                  <button type="button" class="btn-ok" disabled={busy === c.id} onclick={() => cambiarEstado(c.id, 'ATENDIDA')}>Atendida</button>
                  <button type="button" class="btn-warn" disabled={busy === c.id} onclick={() => cambiarEstado(c.id, 'NO_ASISTIO')}>No asistió</button>
                {/if}
              </td>
            {/if}
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</DashCard>

<style>
  .btn-sm, .btn-ok, .btn-warn { border-radius: 999px; cursor: pointer; padding: 0.2rem 0.55rem; font-size: 0.7rem; margin-right: 0.25rem; font-weight: 600; transition: all 0.2s; }
  .btn-sm { border: 1px solid var(--glass-border); background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); color: var(--text-secondary); }
  .btn-sm:hover { background: var(--brand-surface); border-color: var(--brand-strong); color: var(--brand-deep); }
  .btn-ok { border: 0; background: linear-gradient(135deg, #10b981, #059669); color: #fff; }
  .btn-ok:hover { box-shadow: 0 4px 12px rgba(16,185,129,0.35); transform: translateY(-1px); }
  .btn-warn { border: 0; background: linear-gradient(135deg, #d97706, #f59e0b); color: #78350f; }
  .btn-warn:hover { box-shadow: 0 4px 12px rgba(245,158,11,0.35); transform: translateY(-1px); }
  .acts { white-space: nowrap; }
  .err, .empty { margin: 0; font-size: 0.85rem; color: var(--text-secondary); }
  .err { color: var(--danger); }
</style>
